import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  ArrowLeft,
  FolderSync,
  Database
} from 'lucide-react';
import { ProspectLoan } from './types';
import { EncompassEfolderTab } from './EncompassEfolderTab';
import { DataExtractionTab } from './DataExtractionTab';
import { useEncompassDocuments } from '@/hooks/useEncompassDocuments';

interface LoanDocumentsPageProps {
  loan: ProspectLoan;
  onBack: () => void;
}

export function LoanDocumentsPage({ loan, onBack }: LoanDocumentsPageProps) {
  const [activeTab, setActiveTab] = useState<string>('encompass');

  const { summary: encompassSummary } = useEncompassDocuments(
    activeTab === 'encompass' ? loan.encompass_loan_id : ''
  );

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Live Loans
          </Button>
          <div className="border-l pl-4 flex-1">
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Documents - Loan {loan.trans_details_loan_number}
            </h1>
            <p className="text-sm text-gray-500">
              {loan.borrower_first_name} {loan.borrower_last_name}
              {loan.property_address && ` • ${loan.property_address}`}
            </p>
          </div>

          {/* Encompass doc count - shown when encompass tab active */}
          {activeTab === 'encompass' && encompassSummary && (
            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">
                {encompassSummary.attachmentsCount} docs
              </span>
              {encompassSummary.downloadedCount > 0 && (
                <Badge
                  variant="outline"
                  className="text-green-600 border-green-200 bg-green-50/50 text-xs px-1.5 py-0"
                >
                  {encompassSummary.downloadedCount}✓
                </Badge>
              )}
              {encompassSummary.pendingCount > 0 && (
                <Badge
                  variant="outline"
                  className="text-yellow-600 border-yellow-200 bg-yellow-50/50 text-xs px-1.5 py-0"
                >
                  {encompassSummary.pendingCount}⏳
                </Badge>
              )}
              {encompassSummary.failedCount > 0 && (
                <Badge
                  variant="outline"
                  className="text-red-600 border-red-200 bg-red-50/50 text-xs px-1.5 py-0"
                >
                  {encompassSummary.failedCount}⚠
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabbed Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b bg-white px-6">
          <TabsList className="h-10">
            <TabsTrigger value="encompass" className="gap-2">
              <FolderSync className="h-4 w-4" />
              Encompass eFolder
            </TabsTrigger>
            <TabsTrigger value="extraction" className="gap-2">
              <Database className="h-4 w-4" />
              Data Extraction
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="encompass"
          className="flex-1 min-h-0 overflow-hidden flex flex-col"
        >
          <EncompassEfolderTab loanId={loan.encompass_loan_id} />
        </TabsContent>

        <TabsContent
          value="extraction"
          className="flex-1 min-h-0 overflow-hidden flex flex-col mt-0"
        >
          <DataExtractionTab loanId={loan.encompass_loan_id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
